import React, { useState, useEffect, Suspense, lazy } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom'
import { Toaster } from 'react-hot-toast'
import { AuthProvider } from './shared/contexts/AuthContext'
import { ThemeProvider } from './shared/contexts/ThemeContext'
import { supabase } from './shared/api/supabase'
import { QueryProvider } from './lib/react-query/QueryProvider'

import { ProtectedRoute } from './components/layout/ProtectedRoute'
import Layout from './components/layout/Layout'
import { PWAInstallPrompt, ErrorBoundary, ServiceWorkerUpdate } from './components/common'
import NotFoundPage from './pages/NotFoundPage'
import { debug } from './lib/utils/debug'
import FeatureGate from './components/common/FeatureGate'

// Import theme styles (SALT unified system lives in ./styles/index.css + theme-variables.css)


// Lazy load pages for better bundle splitting
const AuthPage = lazy(() => import('./pages/auth').then(module => ({ default: module.AuthPage })))
const HomePage = lazy(() => import('./pages/home').then(module => ({ default: module.HomePage })))
const JournalPage = lazy(() => import('./pages/journal').then(module => ({ default: module.JournalPage })))
const EventsPage = lazy(() => import('./pages/events').then(module => ({ default: module.EventsPage })))
const ProfilePage = lazy(() => import('./pages/profile').then(module => ({ default: module.ProfilePage })))
const CurriculumPage = lazy(() => import('./pages/curriculum').then(module => ({ default: module.CurriculumPage })))
const TestPage = lazy(() => import('./pages/test').then(module => ({ default: module.TestPage })))

// Groups
const GroupManagementPage = lazy(() => import('./pages/groups').then(module => ({ default: module.GroupManagementPage })))
const EditGroupPage = lazy(() => import('./pages/groups').then(module => ({ default: module.EditGroupPage })))
const GroupsOverviewPage = lazy(() => import('./pages/groups/GroupsOverviewPage'))
const TodaysLessonPage = lazy(() => import('./pages/groups/TodaysLessonPage'))
const LessonByDatePage = lazy(() => import('./pages/groups/LessonByDatePage'))

// Tasks and Messaging
const TasksPage = lazy(() => import('./pages/tasks').then(module => ({ default: module.TasksPage })))
const MessagesPage = lazy(() => import('./pages/messaging').then(module => ({ default: module.MessagesPage })))

// Admin pages
const SuperAdminPage = lazy(() => import('./pages/admin').then(module => ({ default: module.SuperAdminPage })))
const AdminPage = lazy(() => import('./pages/admin/AdminPage'))
const EnterpriseAdminPage = lazy(() => import('./pages/admin/EnterpriseAdminPage'))

// Curriculum
const LessonCustomizationPage = lazy(() => import('./pages/curriculum').then(module => ({ default: module.LessonCustomizationPage })))
const TaskCustomizationPage = lazy(() => import('./pages/curriculum').then(module => ({ default: module.TaskCustomizationPage })))
const CurriculumDateCustomizationPage = lazy(() => import('./pages/curriculum/CurriculumDateCustomizationPage'))
const WeeklySchedulePage = lazy(() => import('./pages/schedule').then(module => ({ default: module.WeeklySchedulePage })))
const CurriculumBuilderPage = lazy(() => import('./pages/curriculum').then(module => ({ default: module.CurriculumBuilderPage })))
const CurriculumPreviewPage = lazy(() => import('./pages/curriculum').then(module => ({ default: module.CurriculumPreviewPage })))
const CurriculumSchedulePage = lazy(() => import('./pages/curriculum').then(module => ({ default: module.CurriculumSchedulePage })))
const GroupLessonPage = lazy(() => import('./pages/curriculum/GroupLessonPage'))
const ModernCurriculumDemoPage = lazy(() => import('./pages/curriculum/ModernCurriculumDemoPage'))

// Test pages
const VirtuosoTestPage = lazy(() => import('./pages/test').then(module => ({ default: module.VirtuosoTestPage })))
const PermissionTestPage = lazy(() => import('./pages/test').then(module => ({ default: module.PermissionTestPage })))
const RichTextEditorDemo = lazy(() => import('./pages/test').then(module => ({ default: module.RichTextEditorDemo })))
const OAuthTestPage = lazy(() => import('./pages/test').then(module => ({ default: module.OAuthTestPage })))

// Other pages
const InviteSignup = lazy(() => import('./components/auth/InviteSignup'))
const MarketplaceCurriculumEditPage = lazy(() => import('./pages/marketplace/MarketplaceCurriculumEditPage'))
const GoalTrackingPage = lazy(() => import('./pages/goals/GoalTrackingPage'))
const ContentModerationPanel = lazy(() => import('./components/moderation/ContentModerationPanel'))

// Loading component for lazy-loaded routes
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-salt-gold-dark mx-auto mb-4"></div>
      <p className="text-gray-600">Loading...</p>
    </div>
  </div>
)

// Auth callback component to handle OAuth redirects
function AuthCallback() {
  const navigate = useNavigate()
  const [error, setError] = useState<string | null>(null)
  
  useEffect(() => {
    const handleCallback = async () => {
      try {
        // Get the hash fragment
        const hashParams = new URLSearchParams(window.location.hash.substring(1))
        const errorParam = hashParams.get('error')
        const errorDescription = hashParams.get('error_description')
        
        if (errorParam) {
          debug.error('OAuth error:', errorParam, errorDescription)
          setError(errorDescription || errorParam)
          setTimeout(() => navigate('/auth'), 3000)
          return
        }
        
        // Wait for Supabase to process the callback
        const { data: { session }, error: sessionError } = await supabase.auth.getSession()
        
        if (sessionError) {
          debug.error('Session error:', sessionError)
          setError(sessionError.message)
          setTimeout(() => navigate('/auth'), 3000)
          return
        }
        
        if (session) {
          debug.log('OAuth callback successful, redirecting to home')
          navigate('/')
        } else {
          // If no session after a moment, redirect to auth
          setTimeout(() => {
            navigate('/auth')
          }, 1000)
        }
      } catch (err) {
        debug.error('Callback error:', err)
        setError('An error occurred during sign in')
        setTimeout(() => navigate('/auth'), 3000)
      }
    }
    
    handleCallback()
  }, [navigate])
  
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        {error ? (
          <>
            <div className="text-red-600 mb-4">
              <p className="font-semibold">Sign in failed</p>
              <p className="text-sm mt-2">{error}</p>
            </div>
            <p className="text-sm text-gray-600">Redirecting to sign in page...</p>
          </>
        ) : (
          <>
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-salt-gold-dark mx-auto mb-4"></div>
            <p className="text-gray-600">Completing sign in...</p>
          </>
        )}
      </div>
    </div>
  )
}

function App() {
  return (
    <ErrorBoundary>
      <QueryProvider>
        <AuthProvider>
          <ThemeProvider>
            <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
              <ServiceWorkerUpdate />
              <Toaster 
            position="top-right" 
            toastOptions={{
              className: 'dark:bg-gray-800 dark:text-white dark:border-gray-700',
              style: {
                background: 'var(--toast-bg)',
                color: 'var(--toast-color)',
                border: '1px solid var(--toast-border)',
                borderRadius: '8px',
                padding: '12px 16px',
                fontSize: '14px',
                fontWeight: '500',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                zIndex: 999999,
              },
              duration: 4000,
              success: {
                iconTheme: {
                  primary: '#10b981',
                  secondary: '#ffffff',
                },
              },
              error: {
                iconTheme: {
                  primary: '#ef4444',
                  secondary: '#ffffff',
                },
              },
            }}
          />
          <Routes>
            <Route path="/auth" element={
              <Suspense fallback={<PageLoader />}>
                <AuthPage />
              </Suspense>
            } />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/signup" element={
              <Suspense fallback={<PageLoader />}>
                <InviteSignup />
              </Suspense>
            } />
            <Route path="/virtuoso-test" element={
              <Suspense fallback={<PageLoader />}>
                <VirtuosoTestPage />
              </Suspense>
            } />
            <Route path="/test/permissions" element={
              <Suspense fallback={<PageLoader />}>
                <PermissionTestPage />
              </Suspense>
            } />
            <Route path="/test/rich-text-editor" element={
              <Suspense fallback={<PageLoader />}>
                <RichTextEditorDemo />
              </Suspense>
            } />
            <Route path="/test/oauth" element={
              <Suspense fallback={<PageLoader />}>
                <OAuthTestPage />
              </Suspense>
            } />
                         <Route path="/modern-curriculum-builder" element={
               <Suspense fallback={<PageLoader />}>
                 <ModernCurriculumDemoPage />
               </Suspense>
             } />
             {/* Modern theme demo disabled to keep the app visually consistent */}
            {/* Root redirect - handle before Layout to avoid blank screen */}
            <Route path="/" element={<Navigate to="/home" replace />} />
            <Route
              path="/*"
              element={
                <ProtectedRoute>
                  <Layout />
                </ProtectedRoute>
              }
            >
              <Route path="home" element={
                <Suspense fallback={<PageLoader />}>
                  <HomePage />
                </Suspense>
              } />
              <Route path="journal" element={
                <Suspense fallback={<PageLoader />}>
                  <FeatureGate 
                    feature="journaling"
                    fallback={
                      <div className="flex items-center justify-center min-h-screen">
                        <div className="text-center">
                          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Journaling Not Available</h2>
                          <p className="text-gray-600 dark:text-gray-400">This feature has been disabled by your administrator.</p>
                        </div>
                      </div>
                    }
                  >
                    <JournalPage />
                  </FeatureGate>
                </Suspense>
              } />
              <Route path="profile" element={
                <Suspense fallback={<PageLoader />}>
                  <ProfilePage />
                </Suspense>
              } />
              <Route path="profile/preferences" element={
                <Suspense fallback={<PageLoader />}>
                  <ProfilePage />
                </Suspense>
              } />
              <Route path="groups" element={
                <Suspense fallback={<PageLoader />}>
                  <GroupsOverviewPage />
                </Suspense>
              } />
              <Route path="groups/:groupId" element={
                <Suspense fallback={<PageLoader />}>
                  <GroupManagementPage />
                </Suspense>
              } />
              <Route path="groups/:groupId/edit" element={
                <Suspense fallback={<PageLoader />}>
                  <EditGroupPage />
                </Suspense>
              } />
              <Route path="groups/:groupId/lessons/:adoptionId/customize" element={
                <Suspense fallback={<PageLoader />}>
                  <LessonCustomizationPage />
                </Suspense>
              } />
              <Route path="groups/:groupId/tasks/:adoptionId/customize" element={
                <Suspense fallback={<PageLoader />}>
                  <TaskCustomizationPage />
                </Suspense>
              } />
              <Route path="groups/:groupId/curriculum/:adoptionId/customize" element={
                <Suspense fallback={<PageLoader />}>
                  <CurriculumDateCustomizationPage />
                </Suspense>
              } />
              <Route path="groups/:groupId/schedule/:adoptionId" element={
                <Suspense fallback={<PageLoader />}>
                  <WeeklySchedulePage />
                </Suspense>
              } />

              <Route path="groups/:groupId/curriculum/:adoptionId/lesson/:lessonId" element={
                <Suspense fallback={<PageLoader />}>
                  <GroupLessonPage />
                </Suspense>
              } />
              <Route path="groups/:groupId/today" element={
                <Suspense fallback={<PageLoader />}>
                  <TodaysLessonPage />
                </Suspense>
              } />
              <Route path="groups/:groupId/lesson/:date" element={
                <Suspense fallback={<PageLoader />}>
                  <LessonByDatePage />
                </Suspense>
              } />
              <Route path="events" element={
                <Suspense fallback={<PageLoader />}>
                  <EventsPage />
                </Suspense>
              } />
              <Route path="tasks" element={
                <Suspense fallback={<PageLoader />}>
                  <TasksPage />
                </Suspense>
              } />
              <Route path="messages" element={
                <Suspense fallback={<PageLoader />}>
                  <FeatureGate 
                    feature="messaging_system"
                    fallback={
                      <div className="flex items-center justify-center min-h-screen">
                        <div className="text-center">
                          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Messaging Not Available</h2>
                          <p className="text-gray-600 dark:text-gray-400">This feature has been disabled by your administrator.</p>
                        </div>
                      </div>
                    }
                  >
                    <MessagesPage />
                  </FeatureGate>
                </Suspense>
              } />
              <Route
                path="super-admin"
                element={
                  <ProtectedRoute requireSuperAdmin>
                    <Suspense fallback={<PageLoader />}>
                      <SuperAdminPage />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="admin"
                element={
                  <ProtectedRoute requireAdmin>
                    <Suspense fallback={<PageLoader />}>
                      <AdminPage />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="enterprise-admin"
                element={
                  <ProtectedRoute requireSuperAdmin>
                    <Suspense fallback={<PageLoader />}>
                      <EnterpriseAdminPage />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="curriculum"
                element={
                  <ProtectedRoute>
                    <Suspense fallback={<PageLoader />}>
                      <CurriculumPage />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route path="test" element={
                <Suspense fallback={<PageLoader />}>
                  <TestPage />
                </Suspense>
              } />
              
              {/* Marketplace redirects to Curriculum with Discover tab */}
              <Route path="marketplace" element={
                <Navigate to="/curriculum" replace />
              } />
              <Route path="goals" element={
                <Suspense fallback={<PageLoader />}>
                  <GoalTrackingPage />
                </Suspense>
              } />
            </Route>
            
            {/* Full-Screen Routes - No sidebar for better UX */}
            <Route
              path="/curriculum/builder"
              element={
                <ProtectedRoute requireManager>
                  <Suspense fallback={<PageLoader />}>
                    <CurriculumBuilderPage />
                  </Suspense>
                </ProtectedRoute>
              }
            />
            <Route
              path="/curriculum/:curriculumId/builder"
              element={
                <ProtectedRoute requireManager>
                  <Suspense fallback={<PageLoader />}>
                    <CurriculumBuilderPage />
                  </Suspense>
                </ProtectedRoute>
              }
            />
            <Route
              path="/marketplace/curriculum/:id/edit"
              element={
                <ProtectedRoute>
                  <Suspense fallback={<PageLoader />}>
                    <MarketplaceCurriculumEditPage />
                  </Suspense>
                </ProtectedRoute>
              }
            />
            <Route
              path="/curriculum/:curriculumId/preview"
              element={
                <ProtectedRoute>
                  <Suspense fallback={<PageLoader />}>
                    <CurriculumPreviewPage />
                  </Suspense>
                </ProtectedRoute>
              }
            />
            <Route
              path="/curriculum/:curriculumId/schedule-preview"
              element={
                <ProtectedRoute>
                  <Suspense fallback={<PageLoader />}>
                    <CurriculumSchedulePage />
                  </Suspense>
                </ProtectedRoute>
              }
            />
            
            {/* Admin Routes - Keep outside Layout for full-screen access */}
            <Route path="/admin/moderation" element={
              <ProtectedRoute requireAdmin>
                <Suspense fallback={<PageLoader />}>
                  <ContentModerationPanel />
                </Suspense>
              </ProtectedRoute>
            } />
            
            {/* 404 - Catch all unmatched routes */}
            <Route path="*" element={
              <Suspense fallback={<PageLoader />}>
                <NotFoundPage />
              </Suspense>
            } />
          </Routes>
          <PWAInstallPrompt />
        </Router>
      </ThemeProvider>
    </AuthProvider>
    </QueryProvider>
    </ErrorBoundary>
  )
}

export default App
